import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { FORMAT_NAME_KEYS, FORMAT_SIZE, SHARE_FORMATS, type ShareFormat } from './share-formats';

/** How wide every format's sample is drawn, in CSS pixels: they all share the width, as in the file. */
const SAMPLE_WIDTH = 40;

interface FormatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: ShareFormat | null;
  onChange: (format: ShareFormat) => void;
}

/**
 * Picking the shape the media is made in, from the editor's side column: each
 * format with its name, a sample of its shape and its size in pixels. Picking
 * one closes the dialog.
 */
export function FormatDialog({ open, onOpenChange, value, onChange }: FormatDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="share-format-dialog">
        <DialogHeader>
          <DialogTitle>{t('shareClimb.format')}</DialogTitle>
          <DialogDescription>{t('shareClimb.formatHint')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={t('shareClimb.format')}>
          {SHARE_FORMATS.map((format) => {
            const size = FORMAT_SIZE[format];
            const selected = format === value;
            return (
              <button
                key={format}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => { onChange(format); onOpenChange(false); }}
                className={`flex flex-col items-center gap-2 rounded-lg border p-3 transition-colors ${
                  selected ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'hover:bg-accent'
                }`}
                data-testid={`share-format-${format}`}
              >
                <div className="flex h-20 items-center justify-center" aria-hidden>
                  <div
                    className={`rounded-sm border-2 ${selected ? 'border-primary bg-primary/20' : 'border-muted-foreground/60 bg-muted'}`}
                    style={{ width: SAMPLE_WIDTH, height: (SAMPLE_WIDTH * size.height) / size.width }}
                  />
                </div>
                <span className="text-sm font-medium">{t(FORMAT_NAME_KEYS[format])}</span>
                <span className="text-xs text-muted-foreground">
                  {t('shareClimb.formatPixels', { width: size.width, height: size.height })}
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
